import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Sparkles, X, Maximize2, RotateCcw } from 'lucide-react';
import { usePermissions } from '../../hooks/usePermissions';
import AssistantChat from './AssistantChat';
import { EVENTO_STATO_ASSISTENTE, leggiStatoAssistente } from '../../services/assistenteStato';
import {
  PosizioneBolla, POSIZIONE_PREDEFINITA, dimenticaPosizione, eTrascinamento, leggiPosizione,
  limitaPosizione, salvaPosizione, sposta, stilePannello,
} from '../../utils/posizioneBolla';

const finestra = () => ({ larghezza: window.innerWidth, altezza: window.innerHeight });

const AssistantBubble: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [attivo, setAttivo] = useState(true);
  const [posizione, setPosizione] = useState<PosizioneBolla>(() => limitaPosizione(leggiPosizione(), finestra()));
  const [, setMisure] = useState(finestra());
  const trascinamento = useRef<{ x: number; y: number; da: PosizioneBolla; spostata: boolean } | null>(null);
  const appenaTrascinata = useRef(false);
  const posizioneRef = useRef(posizione);
  posizioneRef.current = posizione;
  const location = useLocation();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();

  // Only show on admin pages
  const isAdminPage = location.pathname.startsWith('/admin');

  // Don't show on the dedicated assistant page
  const isAssistantPage = location.pathname === '/admin/assistente';

  // Check permission
  const canUse = hasPermission('assistant.use');

  // Acceso o spento (impostazioni dell'assistente o card in /admin/settings); se la lettura fallisce resta visibile
  useEffect(() => {
    if (!isAdminPage || !canUse) return;
    let annullato = false;
    leggiStatoAssistente().then(v => { if (!annullato) setAttivo(v); }).catch(() => {});
    const suCambio = (e: Event) => setAttivo((e as CustomEvent).detail?.enabled !== false);
    window.addEventListener(EVENTO_STATO_ASSISTENTE, suCambio);
    return () => { annullato = true; window.removeEventListener(EVENTO_STATO_ASSISTENTE, suCambio); };
  }, [isAdminPage, canUse]);

  // Finestra ridimensionata: l'icona resta dentro lo schermo e il pannello si ricalcola
  useEffect(() => {
    const suRidimensiona = () => {
      setMisure(finestra());
      setPosizione(p => limitaPosizione(p, finestra()));
    };
    window.addEventListener('resize', suRidimensiona);
    return () => window.removeEventListener('resize', suRidimensiona);
  }, []);

  // Close bubble when navigating to assistant page
  useEffect(() => {
    if (isAssistantPage && open) {
      setOpen(false);
    }
  }, [isAssistantPage, open]);

  const handleConversationCreated = useCallback((id: string) => {
    setConversationId(id);
  }, []);

  const handleOpenFullPage = () => {
    setOpen(false);
    navigate('/admin/assistente');
  };

  const rimettiAlPosto = () => {
    dimenticaPosizione();
    setPosizione(limitaPosizione(POSIZIONE_PREDEFINITA, finestra()));
  };

  const suPuntatoreGiu = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (e.button !== 0) return;
    trascinamento.current = { x: e.clientX, y: e.clientY, da: posizione, spostata: false };
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const suPuntatoreMosso = (e: React.PointerEvent<HTMLButtonElement>) => {
    const t = trascinamento.current;
    if (!t) return;
    const dx = e.clientX - t.x;
    const dy = e.clientY - t.y;
    if (!t.spostata && !eTrascinamento(dx, dy)) return;
    t.spostata = true;
    setPosizione(sposta(t.da, dx, dy, finestra()));
  };

  const suPuntatoreSu = (e: React.PointerEvent<HTMLButtonElement>, annullato = false) => {
    const t = trascinamento.current;
    trascinamento.current = null;
    if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId);
    if (t?.spostata) {
      appenaTrascinata.current = true; // il click che segue il trascinamento non apre né chiude
      // pointercancel non porta coordinate affidabili: vale l'ultima posizione mostrata
      const finale = annullato ? posizioneRef.current : sposta(t.da, e.clientX - t.x, e.clientY - t.y, finestra());
      setPosizione(finale);
      salvaPosizione(finale);
    }
  };

  const suClick = () => {
    if (appenaTrascinata.current) {
      appenaTrascinata.current = false;
      return;
    }
    setOpen(prev => !prev);
  };

  if (!isAdminPage || isAssistantPage || !canUse || !attivo) {
    return null;
  }

  const spostata = posizione.destra !== POSIZIONE_PREDEFINITA.destra || posizione.basso !== POSIZIONE_PREDEFINITA.basso;

  return (
    <>
      {/* Chat panel */}
      {open && (
        <div
          className="fixed z-50 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 flex flex-col overflow-hidden animate-in"
          style={{
            ...stilePannello(posizione, finestra()),
            animation: 'slideUp 0.2s ease-out',
          }}
        >
          {/* Panel header */}
          <div className="flex items-center justify-between px-3 py-2.5 bg-indigo-600 text-white">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4" />
              <span className="text-sm font-medium">Assistente UNASCA</span>
            </div>
            <div className="flex items-center gap-1">
              {spostata && (
                <button
                  onClick={rimettiAlPosto}
                  className="p-1.5 rounded hover:bg-indigo-700 transition-colors"
                  title="Rimetti l'icona in basso a destra"
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                </button>
              )}
              <button
                onClick={handleOpenFullPage}
                className="p-1.5 rounded hover:bg-indigo-700 transition-colors"
                title="Apri in pagina dedicata"
              >
                <Maximize2 className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => setOpen(false)}
                className="p-1.5 rounded hover:bg-indigo-700 transition-colors"
                title="Chiudi"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

          {/* Chat content */}
          <div className="flex-1 overflow-hidden">
            <AssistantChat
              mode="bubble"
              conversationId={conversationId}
              onConversationCreated={handleConversationCreated}
            />
          </div>
        </div>
      )}

      {/* Floating button: un tocco apre/chiude, trascinandolo si sposta (la posizione resta salvata nel browser) */}
      <button
        onClick={suClick}
        onPointerDown={suPuntatoreGiu}
        onPointerMove={suPuntatoreMosso}
        onPointerUp={suPuntatoreSu}
        onPointerCancel={e => suPuntatoreSu(e, true)}
        style={{ right: posizione.destra, bottom: posizione.basso, touchAction: 'none' }}
        className={`fixed z-50 w-12 h-12 rounded-full shadow-lg flex items-center justify-center transition-colors duration-200 cursor-grab active:cursor-grabbing select-none ${
          open
            ? 'bg-gray-600 hover:bg-gray-700'
            : 'bg-indigo-600 hover:bg-indigo-700'
        }`}
        title={open ? 'Chiudi assistente (trascina per spostare)' : 'Apri assistente (trascina per spostare)'}
        aria-label={open ? 'Chiudi assistente' : 'Apri assistente'}
      >
        {open ? (
          <X className="w-5 h-5 text-white pointer-events-none" />
        ) : (
          <Sparkles className="w-5 h-5 text-white pointer-events-none" />
        )}
      </button>

      {/* Animation keyframes */}
      <style>{`
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </>
  );
};

export default AssistantBubble;
